"use client";

import clsx from "clsx";
import type React from "react";
import Link from "src/components/common/Link/Link";
import Ripple from "src/components/common/Ripple/Ripple";
import Label from "src/components/form/Label/Label";
import styles from "./SidebarNavigation.module.scss";

export interface SidebarNavigationProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
}

export interface SidebarNavigationGroupProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
}

export interface SidebarNavigationLabelProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode;
}

export interface SidebarNavigationListProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export interface SidebarNavigationItemProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children" | "color"> {
  children: React.ReactNode;
  depth?: number;
  href?: string;
  icon?: React.ReactNode;
  ripple?: boolean;
  selected?: boolean;
  /** Only applies to links. */
  target?: "_blank";
  trailing?: React.ReactNode;
}

const SidebarNavigationGroup = (props: SidebarNavigationGroupProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <section className={clsx(styles.group, className)} {...otherProps}>
      {children}
    </section>
  );
};

const SidebarNavigationLabel = (props: SidebarNavigationLabelProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <Label as="span" tone="muted" className={clsx(styles.label, className)} {...otherProps}>
      {children}
    </Label>
  );
};

const SidebarNavigationList = (props: SidebarNavigationListProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <div className={clsx(styles.list, className)} {...otherProps}>
      {children}
    </div>
  );
};

const SidebarNavigationItemContent = (props: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  trailing?: React.ReactNode;
}) => {
  const { children, icon, trailing } = props;
  return (
    <>
      {icon ? <span className={styles.icon}>{icon}</span> : null}
      <span className={styles.content}>{children}</span>
      {trailing ? <span className={styles.trailing}>{trailing}</span> : null}
    </>
  );
};

const SidebarNavigationItem = (props: SidebarNavigationItemProps) => {
  const {
    children,
    className,
    depth = 0,
    href,
    icon,
    ripple = true,
    selected = false,
    target,
    trailing,
    type = "button",
    ...otherProps
  } = props;
  const itemClassName = clsx(styles.item, className);
  const style = {
    ...otherProps.style,
    paddingInlineStart: `calc(0.625rem + ${depth} * 0.75rem)`,
  };
  const content = (
    <>
      {ripple ? <Ripple /> : null}
      <SidebarNavigationItemContent icon={icon} trailing={trailing}>
        {children}
      </SidebarNavigationItemContent>
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        aria-current={selected ? "page" : undefined}
        className={itemClassName}
        data-selected={selected ? "true" : undefined}
        style={style}
        {...(otherProps as React.ComponentProps<typeof Link>)}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={itemClassName}
      data-selected={selected ? "true" : undefined}
      style={style}
      {...otherProps}
    >
      {content}
    </button>
  );
};

const SidebarNavigation = (props: SidebarNavigationProps) => {
  const { children, className, ...otherProps } = props;
  return (
    <nav className={clsx(styles.navigation, className)} {...otherProps}>
      {children}
    </nav>
  );
};

SidebarNavigation.Group = SidebarNavigationGroup;
SidebarNavigation.Item = SidebarNavigationItem;
SidebarNavigation.Label = SidebarNavigationLabel;
SidebarNavigation.List = SidebarNavigationList;

export default SidebarNavigation;
